import { useEffect, useRef, useState } from "react";
import { Reveal, SectionHeading } from "@/components/Shared";
import { BOOKING_URL } from "@/config";

const CALENDLY_SCRIPT = "https://assets.calendly.com/assets/external/widget.js";

const loadCalendlyScript = () =>
  new Promise((resolve, reject) => {
    if (window.Calendly) return resolve(window.Calendly);
    const existing = document.querySelector(`script[src="${CALENDLY_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Calendly), { once: true });
      existing.addEventListener("error", reject, { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = CALENDLY_SCRIPT;
    script.async = true;
    script.onload = () => resolve(window.Calendly);
    script.onerror = () => reject(new Error("Calendly widget script failed to load"));
    document.body.appendChild(script);
  });

export const BookingSection = () => {
  const containerRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadCalendlyScript()
      .then((calendly) => {
        if (cancelled || !containerRef.current || !calendly) return;
        containerRef.current.replaceChildren();
        calendly.initInlineWidget({ url: BOOKING_URL, parentElement: containerRef.current });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    const timer = setTimeout(() => {
      if (!cancelled && containerRef.current && !containerRef.current.querySelector("iframe")) {
        setFailed(true);
      }
    }, 8000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (containerRef.current) containerRef.current.replaceChildren();
    };
  }, []);

  return (
    <section id="book-audit" className="py-20 sm:py-28" data-testid="booking-section">
      <div className="max-w-4xl mx-auto px-6">
        <SectionHeading
          eyebrow="Book your free audit"
          title="Pick a time that works for you."
          sub="Free 15-minute strategy call — leave with a clear outbound plan for your firm."
        />
        <Reveal delay={0.1} className="mt-10">
          {failed ? (
            <div className="rounded-3xl border border-[#E9E4F0] bg-[#EDEDF1] p-10 text-center" data-testid="booking-fallback">
              <p className="text-[#3B0764] font-medium">The calendar couldn't load here.</p>
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="booking-fallback-link"
                className="mt-5 inline-flex items-center rounded-full bg-violet-700 px-6 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:bg-violet-800"
              >
                Open the booking page
              </a>
            </div>
          ) : (
            <div className="rounded-3xl border border-[#E9E4F0] bg-[#EDEDF1] overflow-hidden shadow-[0_20px_50px_-30px_rgba(91,33,182,0.25)]">
              <div
                ref={containerRef}
                data-testid="calendly-inline-widget"
                aria-label="Calendly booking calendar"
                style={{ minWidth: "320px", height: "700px" }}
              />
            </div>
          )}
        </Reveal>
        <p className="mt-4 text-center text-sm text-[#6B5CA5]">Calendar not loading for you?</p>
        <div className="mt-3 text-center">
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="booking-new-tab-link"
            className="inline-flex items-center gap-2 rounded-full border-2 border-violet-700 px-6 py-3 text-sm font-semibold text-violet-700 transition-colors duration-300 hover:bg-violet-700 hover:text-white"
          >
            Open the booking page directly
          </a>
        </div>
      </div>
    </section>
  );
};
